import classNames from "classnames";
import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { ReactComponent as DownloadIcon } from "assets/images/common/download.svg";
import { ReactComponent as NotFoundIcon } from "assets/images/common/not-found.svg";
import { ReactComponent as PlusIcon } from "assets/images/common/plus.svg";
import { ReactComponent as ReloadIcon } from "assets/images/common/reload.svg";
import { ReactComponent as TrashIcon } from "assets/images/common/trash.svg";

import PageTemplate from "modules/common/PageTemplate";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import Card from "components/Card";
import SearchInput from "components/SearchInput";
import Select from "components/Select";
import Table from "components/Table";

import useDebounce from "hooks/useDebounce";
import useRealtime from "hooks/useRealtime";

import { AssetsApi, CompaniesApi, PortfoliosApi } from "services/http";
import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";

import { BaseOption, ModalType, UserRole } from "entities/common";
import {
  EXPORT_FORMAT_LABELS,
  GENERATION_FAILED_TOOLTIP,
  ReportItem,
  buildReportListQuery,
  clampPage,
  formatGeneratedAt,
  formatPeriod,
  isInFlight,
  scopeLabel,
} from "entities/reports";
import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { showModal } from "store/slices/modalSlice";
import { downloadReportRequest, getReportListRequest, retryReportRequest } from "store/slices/reportsSlice";

import { FORMAT_OPTIONS } from "./data";

import styles from "./style.module.scss";

const SELECT_PAGE_SIZE = 50;

// Browsers refuse to break a line after a hyphen that precedes a digit, so the
// date range needs an explicit break opportunity to wrap in its narrow column.
const renderPeriod = (item: ReportItem) => {
  const [from, to] = formatPeriod(item.period_from, item.period_to).split("-");
  return (
    <>
      {from}-<wbr />
      {to}
    </>
  );
};

const TABLE_HEADERS = [
  { label: "Report name", key: "name" },
  { label: "Type", key: "type" },
  { label: "Scope", key: "scope" },
  { label: "Company", key: "company" },
  { label: "Period", key: "period" },
  { label: "Format", key: "format" },
  { label: "Date of generation", key: "generatedAt" },
  { label: "Actions", key: "actions" },
];

const ReportsPage: FC = () => {
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search, 500);

  const [companyId, setCompanyId] = useState("");
  const [companyLabel, setCompanyLabel] = useState("All companies");
  const [portfolioId, setPortfolioId] = useState("");
  const [portfolioLabel, setPortfolioLabel] = useState("All portfolios");
  const [assetId, setAssetId] = useState("");
  const [assetLabel, setAssetLabel] = useState("All assets");
  const [formatValue, setFormatValue] = useState("");

  const { list, meta, hasReports, listLoaded } = useAppSelector((state: RootStore) => state.reports);
  const authUser = useAppSelector((state: RootStore) => state.auth.user);
  const isAdmin = authUser?.role === UserRole.ADMINISTRATOR;

  const query = useMemo(
    () =>
      buildReportListQuery({
        search: debouncedSearch,
        companyId,
        portfolioId,
        assetId,
        exportFormat: formatValue,
        page,
        pageSize: meta.page_size,
      }),
    [debouncedSearch, companyId, portfolioId, assetId, formatValue, page, meta.page_size],
  );

  useEffect(() => {
    dispatch(getReportListRequest(query));
  }, [dispatch, query]);

  useEffect(() => {
    if (!listLoaded || list.length > 0) return;

    const clamped = clampPage(page, meta.total, meta.page_size);
    if (clamped !== page) setPage(clamped);
  }, [listLoaded, list.length, page, meta.total, meta.page_size]);

  const reload = useCallback(() => {
    dispatch(getReportListRequest(query, { silent: true }));
  }, [dispatch, query]);

  useRealtime(["reports"], reload);

  const searchCompanies = useCallback(async (searchTerm: string, pageNum: number) => {
    try {
      const { data } = await CompaniesApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.SEARCH]: searchTerm || undefined,
          [QUERY_PARAMS.PAGE]: pageNum,
          [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
        }),
      );
      const fetched: BaseOption[] = (data?.items ?? []).map((company) => ({
        value: String(company.id),
        label: company.name,
      }));
      const withSentinel = pageNum === 1 && !searchTerm.trim();
      return {
        options: withSentinel ? [{ value: "", label: "All companies" }, ...fetched] : fetched,
        hasMore: (data?.total ?? 0) > pageNum * SELECT_PAGE_SIZE,
      };
    } catch {
      return { options: [], hasMore: false };
    }
  }, []);

  const searchPortfolios = useCallback(
    async (searchTerm: string, pageNum: number) => {
      try {
        const { data } = await PortfoliosApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: searchTerm || undefined,
            [QUERY_PARAMS.PAGE]: pageNum,
            [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
            ...(companyId ? { [QUERY_PARAMS.COMPANY_ID]: Number(companyId) } : {}),
          }),
        );
        const fetched: BaseOption[] = (data?.items ?? []).map((portfolio) => ({
          value: String(portfolio.id),
          label: portfolio.name,
          companyId: portfolio.company_id,
          companyName: portfolio.company_name,
        }));
        const withSentinel = pageNum === 1 && !searchTerm.trim();
        return {
          options: withSentinel ? [{ value: "", label: "All portfolios" }, ...fetched] : fetched,
          hasMore: (data?.total ?? 0) > pageNum * SELECT_PAGE_SIZE,
        };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [companyId],
  );

  const searchAssets = useCallback(
    async (searchTerm: string, pageNum: number) => {
      try {
        const { data } = await AssetsApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: searchTerm || undefined,
            [QUERY_PARAMS.PAGE]: pageNum,
            [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
            ...(portfolioId ? { [QUERY_PARAMS.PORTFOLIO_ID]: Number(portfolioId) } : {}),
            ...(companyId ? { [QUERY_PARAMS.COMPANY_ID]: Number(companyId) } : {}),
          }),
        );
        const fetched: BaseOption[] = (data?.items ?? []).map((asset) => ({
          value: String(asset.id),
          label: asset.name,
          portfolioId: asset.portfolio_id,
          portfolioName: asset.portfolio_name,
          companyId: asset.company_id,
          companyName: asset.company_name,
        }));
        const withSentinel = pageNum === 1 && !searchTerm.trim();
        return {
          options: withSentinel ? [{ value: "", label: "All assets" }, ...fetched] : fetched,
          hasMore: (data?.total ?? 0) > pageNum * SELECT_PAGE_SIZE,
        };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [companyId, portfolioId],
  );

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  }, []);

  const handleCompanyFilterChange = useCallback(
    (value: string, label: string) => {
      setCompanyId(value);
      setCompanyLabel(value ? label : "All companies");
      if (portfolioId) {
        setPortfolioId("");
        setPortfolioLabel("All portfolios");
      }
      if (assetId) {
        setAssetId("");
        setAssetLabel("All assets");
      }
      setPage(1);
    },
    [assetId, portfolioId],
  );

  const handlePortfolioFilterChange = useCallback(
    (value: string, label: string, option?: BaseOption) => {
      setPortfolioId(value);
      setPortfolioLabel(value ? label : "All portfolios");
      if (value && !companyId && option?.companyId) {
        setCompanyId(String(option.companyId));
        setCompanyLabel((option.companyName as string) ?? "");
      }
      if (assetId) {
        setAssetId("");
        setAssetLabel("All assets");
      }
      setPage(1);
    },
    [assetId, companyId],
  );

  const handleAssetFilterChange = useCallback((value: string, label: string, option?: BaseOption) => {
    setAssetId(value);
    setAssetLabel(value ? label : "All assets");
    if (value && option?.portfolioId) {
      setPortfolioId(String(option.portfolioId));
      setPortfolioLabel((option.portfolioName as string) ?? "");
    }
    if (value && option?.companyId) {
      setCompanyId(String(option.companyId));
      setCompanyLabel((option.companyName as string) ?? "");
    }
    setPage(1);
  }, []);

  const handleFormatFilterChange = useCallback((value: string) => {
    setFormatValue(value);
    setPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setCompanyId("");
    setCompanyLabel("All companies");
    setPortfolioId("");
    setPortfolioLabel("All portfolios");
    setAssetId("");
    setAssetLabel("All assets");
    setFormatValue("");
    setPage(1);
  }, []);

  const handleGenerateReport = () => {
    dispatch(showModal({ modalType: ModalType.GENERATE_REPORT }));
  };

  const handleDownload = useCallback(
    (item: ReportItem) => () => {
      dispatch(downloadReportRequest({ id: item.id, name: item.name, format: item.export_format }));
    },
    [dispatch],
  );

  const handleRetry = useCallback(
    (item: ReportItem) => () => {
      dispatch(retryReportRequest(item.id));
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    (item: ReportItem) => () => {
      dispatch(showModal({ modalType: ModalType.REMOVE_REPORT, modalProps: { id: item.id } }));
    },
    [dispatch],
  );

  const tableHeaders = useMemo(
    () => TABLE_HEADERS.filter((header) => header.key !== "company" || isAdmin),
    [isAdmin],
  );

  const tableData = useMemo(
    () =>
      list.map((item) => ({
        name: (
          <span className={styles.name}>
            <span className={styles.nameTitle}>{item.name}</span>
            {item.is_new && <span className={styles.nameNew}>New</span>}
          </span>
        ),
        type: <span className={styles.type}>{item.report_type}</span>,
        scope: <span className={styles.scope}>{scopeLabel(item)}</span>,
        company: <span className={styles.company}>{item.company_name}</span>,
        period: <span className={styles.period}>{renderPeriod(item)}</span>,
        format: <span className={styles.format}>{EXPORT_FORMAT_LABELS[item.export_format]}</span>,
        generatedAt: isInFlight(item.status) ? (
          <span className={styles.generating}>
            <span className={styles.spinner} />
            Generating
          </span>
        ) : item.status === "failed" ? (
          <span className={styles.failed} title={GENERATION_FAILED_TOOLTIP}>
            Generation failed
            <span className={styles.failedIcon}>i</span>
          </span>
        ) : (
          <span className={styles.generatedAt}>{formatGeneratedAt(item.generated_at)}</span>
        ),
        actions: (
          <div className="table-actions">
            {item.status === "ready" && (
              <Button
                variant="link"
                aria-label="Download report"
                className="table-actions__button"
                onClick={handleDownload(item)}
              >
                <DownloadIcon />
              </Button>
            )}

            {item.status === "failed" && (
              <Button
                variant="link"
                aria-label="Retry report generation"
                className="table-actions__button"
                onClick={handleRetry(item)}
              >
                <ReloadIcon />
              </Button>
            )}

            <Button
              variant="link"
              aria-label="Delete report"
              className="table-actions__button"
              onClick={handleDelete(item)}
            >
              <TrashIcon />
            </Button>
          </div>
        ),
      })),
    [list, handleDownload, handleRetry, handleDelete],
  );

  const totalPages = Math.max(1, Math.ceil(meta.total / meta.page_size));

  return (
    <PageTemplate
      title="Reports"
      description="Generate, view and download reports"
      className={styles.page}
      actions={
        <Button variant="primary" className={styles.create} onClick={handleGenerateReport}>
          <span className={styles.createText}>Generate new report</span>
          <span className={styles.createIcon}>
            <PlusIcon />
          </span>
        </Button>
      }
    >
      <Card className={styles.card}>
        <div className={styles.search}>
          <SearchInput
            name="search"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search reports..."
            className={styles.searchInput}
          />

          <Button variant="secondary" className={styles.filtersToggle} onClick={() => setShowFilters((prev) => !prev)}>
            {showFilters ? "Close filters" : "Open filters"}
          </Button>
        </div>

        {showFilters && (
          <div className={styles.filters}>
            <div className={classNames(styles.filtersWrap, { [styles.filtersWrapCompact]: !isAdmin })}>
              {isAdmin && (
                <AsyncSearchableSelect
                  variant="secondary"
                  value={companyId}
                  selectedLabel={companyLabel}
                  onSearch={searchCompanies}
                  onChange={handleCompanyFilterChange}
                  placeholder="All companies"
                  searchPlaceholder="Search"
                  className={styles.filter}
                />
              )}

              <AsyncSearchableSelect
                variant="secondary"
                value={portfolioId}
                selectedLabel={portfolioLabel}
                onSearch={searchPortfolios}
                onChange={handlePortfolioFilterChange}
                placeholder="All portfolios"
                searchPlaceholder="Search"
                className={styles.filter}
              />

              <AsyncSearchableSelect
                variant="secondary"
                value={assetId}
                selectedLabel={assetLabel}
                onSearch={searchAssets}
                onChange={handleAssetFilterChange}
                placeholder="All assets"
                searchPlaceholder="Search"
                className={styles.filter}
              />

              <Select
                variant="secondary"
                options={FORMAT_OPTIONS}
                value={formatValue}
                onChange={handleFormatFilterChange}
                placeholder="All formats"
                className={styles.filter}
              />
            </div>

            <Button form="circle" variant="white" className={styles.reload} onClick={handleClearFilters}>
              <ReloadIcon />
            </Button>

            <Button variant="white" className={styles.clearFilters} onClick={handleClearFilters}>
              Clear filters
            </Button>
          </div>
        )}

        {listLoaded && !hasReports ? (
          <div className={styles.notFound}>
            <NotFoundIcon />
            <p>No reports yet</p>

            <Button variant="secondary" onClick={handleGenerateReport}>
              Generate first report
            </Button>
          </div>
        ) : listLoaded && list.length === 0 ? (
          <div className={styles.notFound}>
            <NotFoundIcon />
            <p>No reports found</p>
          </div>
        ) : (
          <div className={styles.tableScroll}>
            <Table
              headers={tableHeaders}
              data={tableData}
              pagination={{ total: totalPages, page, onPageChange: setPage }}
              className={styles.table}
            />
          </div>
        )}
      </Card>
    </PageTemplate>
  );
};

export default ReportsPage;
